<script setup lang="ts">
import { ref, watch, nextTick, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCancellationPolicy } from '@/composables/useCancellationPolicy'
import { useUnsavedChanges } from '@/composables/useUnsavedChanges'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import type { CancellationPolicyTierData } from '@/types/cancellation-policy'

const props = defineProps<{
  editionId: number
  dirtyKey?: string
}>()

const { t } = useI18n()
const { policy, loading, saving, error, validationErrors, fetchPolicy, savePolicy } =
  useCancellationPolicy()
const { markDirty, markClean } = useUnsavedChanges()

const isEnabled = ref<boolean>(false)
const cancellationDeadline = ref<string>('')
const tiers = ref<Array<{ valid_until: string; refund_percentage: number }>>([])
const successMessage = ref<string | null>(null)
const initialized = ref(false)

const today = new Date().toISOString().slice(0, 10)

const canAddTier = computed<boolean>(() => tiers.value.length < 10)

const deadlineInPast = computed<boolean>(
  () => cancellationDeadline.value !== '' && cancellationDeadline.value < today,
)

function tierDateInPast(validUntil: string): boolean {
  return validUntil !== '' && validUntil < today
}

function populateFromPolicy(): void {
  if (policy.value) {
    isEnabled.value = policy.value.is_enabled
    cancellationDeadline.value = policy.value.cancellation_deadline ?? ''
    tiers.value = policy.value.tiers.map((tier: CancellationPolicyTierData) => ({
      valid_until: tier.valid_until,
      refund_percentage: tier.refund_percentage,
    }))
  } else {
    isEnabled.value = false
    cancellationDeadline.value = ''
    tiers.value = []
  }
}

watch([isEnabled, cancellationDeadline, tiers], () => {
  if (initialized.value && props.dirtyKey) markDirty(props.dirtyKey)
}, { deep: true })

function addTier(): void {
  if (!canAddTier.value) return
  tiers.value = [...tiers.value, { valid_until: '', refund_percentage: 100 }]
}

function removeTier(index: number): void {
  tiers.value = tiers.value.filter((_, i) => i !== index)
}

function tierValidationError(field: string, index: number): string | null {
  const key = `tiers.${index}.${field}`
  const errs = validationErrors.value[key]
  return errs && errs.length > 0 ? (errs[0] ?? null) : null
}

async function handleSave(): Promise<void> {
  successMessage.value = null
  const success = await savePolicy(props.editionId, {
    is_enabled: isEnabled.value,
    cancellation_deadline: cancellationDeadline.value !== '' ? cancellationDeadline.value : null,
    tiers: tiers.value,
  })
  if (success) {
    if (props.dirtyKey) markClean(props.dirtyKey)
    successMessage.value = t('cancellation_policy.save_success')
    setTimeout(() => {
      successMessage.value = null
    }, 4000)
  }
}

onMounted(async () => {
  await fetchPolicy(props.editionId)
  populateFromPolicy()
  await nextTick()
  initialized.value = true
})

onUnmounted(() => {
  if (props.dirtyKey) markClean(props.dirtyKey)
})
</script>

<template>
  <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
    <div class="flex items-center justify-between mb-6">
      <div>
        <h2 class="text-xl font-bold text-white">{{ t('cancellation_policy.heading') }}</h2>
        <p class="text-sm text-slate-400 mt-1">{{ t('cancellation_policy.description') }}</p>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="flex items-center justify-center py-8">
      <svg class="animate-spin h-6 w-6 text-emerald-500" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    </div>

    <!-- Error state -->
    <div
      v-else-if="error"
      class="rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-4 text-sm text-red-400"
    >
      {{ t('common.error_generic') }}
    </div>

    <template v-else>
      <!-- Enable toggle -->
      <div class="flex items-center justify-between rounded-xl bg-slate-800 border border-slate-700 px-5 py-4 mb-4">
        <div>
          <p class="text-sm font-semibold text-white">{{ t('cancellation_policy.enable_label') }}</p>
          <p class="text-xs text-slate-400 mt-0.5">{{ t('cancellation_policy.enable_hint') }}</p>
        </div>
        <button
          type="button"
          role="switch"
          :aria-checked="isEnabled"
          class="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-slate-800"
          :class="isEnabled ? 'bg-emerald-500' : 'bg-slate-600'"
          @click="isEnabled = !isEnabled"
        >
          <span
            class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
            :class="isEnabled ? 'translate-x-5' : 'translate-x-0'"
          />
        </button>
      </div>

      <!-- Configuration (dimmed when disabled) -->
      <div :class="{ 'opacity-60': !isEnabled }">

        <!-- Cancellation deadline -->
        <div class="mb-4">
          <label
            for="cancellation-deadline"
            class="block text-sm font-medium text-slate-300 mb-1"
          >
            {{ t('cancellation_policy.deadline_label') }}
            <span class="text-slate-500 font-normal ml-1">{{ t('common.optional') }}</span>
          </label>
          <input
            id="cancellation-deadline"
            v-model="cancellationDeadline"
            type="date"
            class="w-full sm:w-64 rounded-lg bg-slate-900 border px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            :class="validationErrors['cancellation_deadline'] ? 'border-red-500' : 'border-slate-600'"
          />
          <p
            v-if="validationErrors['cancellation_deadline']?.[0]"
            class="mt-1 text-xs text-red-400"
          >
            {{ validationErrors['cancellation_deadline'][0] }}
          </p>
          <p v-else-if="deadlineInPast" class="mt-1 text-xs text-amber-400">
            {{ t('cancellation_policy.deadline_past_warning') }}
          </p>
          <p v-else class="mt-1 text-xs text-slate-500">
            {{ t('cancellation_policy.deadline_hint') }}
          </p>
        </div>

        <!-- Refund tiers -->
        <div class="mb-4">
          <div class="flex items-center justify-between mb-3">
            <p class="text-sm font-semibold text-white">{{ t('cancellation_policy.tiers_heading') }}</p>
            <button
              type="button"
              :disabled="!canAddTier"
              class="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400 hover:text-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              @click="addTier"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              {{ t('cancellation_policy.add_tier') }}
            </button>
          </div>

          <!-- Empty tiers -->
          <div
            v-if="tiers.length === 0"
            class="rounded-xl border border-dashed border-slate-700 px-6 py-6 text-center"
          >
            <p class="text-sm text-slate-500">{{ t('cancellation_policy.tiers_empty') }}</p>
          </div>

          <!-- Tier rows -->
          <div v-else class="space-y-2">
            <div
              v-for="(tier, index) in tiers"
              :key="index"
              class="flex flex-col sm:flex-row sm:items-start gap-3 rounded-xl bg-slate-800 border border-slate-700 px-4 py-3"
            >
              <!-- valid_until -->
              <div class="flex-1">
                <label
                  :for="`tier-date-${index}`"
                  class="block text-xs font-medium text-slate-400 mb-1"
                >
                  {{ t('cancellation_policy.tier_valid_until') }}
                </label>
                <input
                  :id="`tier-date-${index}`"
                  v-model="tier.valid_until"
                  type="date"
                  class="w-full rounded-lg bg-slate-900 border px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  :class="tierValidationError('valid_until', index) ? 'border-red-500' : 'border-slate-600'"
                />
                <p
                  v-if="tierValidationError('valid_until', index)"
                  class="mt-1 text-xs text-red-400"
                >
                  {{ tierValidationError('valid_until', index) }}
                </p>
                <p
                  v-else-if="tierDateInPast(tier.valid_until)"
                  class="mt-1 text-xs text-amber-400"
                >
                  {{ t('cancellation_policy.tier_date_past_warning') }}
                </p>
              </div>

              <!-- refund_percentage -->
              <div class="sm:w-36">
                <label
                  :for="`tier-pct-${index}`"
                  class="block text-xs font-medium text-slate-400 mb-1"
                >
                  {{ t('cancellation_policy.tier_refund_percentage') }}
                </label>
                <div class="flex items-center gap-2">
                  <input
                    :id="`tier-pct-${index}`"
                    v-model.number="tier.refund_percentage"
                    type="number"
                    min="0"
                    max="100"
                    class="w-full rounded-lg bg-slate-900 border px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    :class="tierValidationError('refund_percentage', index) ? 'border-red-500' : 'border-slate-600'"
                  />
                  <span class="text-slate-400 text-sm shrink-0">%</span>
                </div>
                <p
                  v-if="tierValidationError('refund_percentage', index)"
                  class="mt-1 text-xs text-red-400"
                >
                  {{ tierValidationError('refund_percentage', index) }}
                </p>
              </div>

              <!-- Remove button -->
              <div class="flex items-end pb-0.5">
                <button
                  type="button"
                  class="text-slate-500 hover:text-red-400 transition-colors p-1 rounded"
                  :aria-label="t('cancellation_policy.remove_tier')"
                  @click="removeTier(index)"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <p v-if="!canAddTier" class="mt-2 text-xs text-slate-500">
            {{ t('cancellation_policy.tiers_max_reached') }}
          </p>
        </div>
      </div>

      <!-- Generic error -->
      <div
        v-if="error && !loading"
        class="mb-4 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
      >
        {{ t('common.error_generic') }}
      </div>

      <!-- Success message -->
      <div
        v-if="successMessage"
        class="mb-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-5 py-3 text-sm text-emerald-400"
      >
        {{ successMessage }}
      </div>

      <!-- Save button -->
      <div class="flex justify-end">
        <button
          type="button"
          :disabled="saving"
          class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
          @click="handleSave"
        >
          <AppSpinner v-if="saving" size="button" />
          {{ saving ? t('common.loading') : t('cancellation_policy.save_button') }}
        </button>
      </div>
    </template>
  </div>
</template>
